import {
  useKineticMap,
  useStableResultData,
  MapContext,
  DEFAULT_ZOOM,
  useMapBoxSearchContext,
  MapSearchResultItem,
  useMapSearchBoxPin,
  MapSearchResultProps,
  useAppSearchHistory,
  publicUrl,
  AppSearchItemType,
  AppSearchIconKey,
  useGetNodes,
} from '@kinetic-ui/shared';
import { DetectorType, useDetectorsQuery } from '@kinetic-ui/data-layer';
import { useCallback, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import CountsIcon from '@mui/icons-material/Pin';
import { ENTITY_DRAWER_VIEW_ALL_ROUTE, ENTITY_EDIT_ROUTE } from '../EntityList/consts';

const MapSearchResult: React.FC<MapSearchResultProps> = ({ isAppSearch, onResultClick }) => {
  const map = useKineticMap();
  const { searchText } = useMapBoxSearchContext();

  const result = useDetectorsQuery({
    fetchPolicy: 'cache-and-network',
    variables: { searchText, limit: 5 },
  });
  const resultData = useStableResultData(result);
  const { selectedLayers, setSelectedLayers, hasEntityDrawer } = useContext(MapContext);
  const { addMapPin, removeMapPin } = useMapSearchBoxPin();

  const detectors = useGetNodes(resultData?.detectors);

  const { push } = useHistory();
  const { addSearchHistory } = useAppSearchHistory();

  const onClick = useCallback(
    (id: string, coordinates: [number, number], openInNewWindow?: boolean) => {
      if (isAppSearch) {
        const path = `/${ENTITY_EDIT_ROUTE}/${id}/`;
        if (openInNewWindow) {
          window.open(publicUrl(path), '_blank');
        } else {
          push(path);
        }
        const detector = detectors.find((e) => e.id === id);
        if (detector?.name) {
          addSearchHistory({
            name: detector.name,
            link: path,
            type: AppSearchItemType.View,
            icon: AppSearchIconKey.Counts,
          });
        }
        if (onResultClick) {
          onResultClick();
        }
        return;
      }

      if (!hasEntityDrawer) {
        addMapPin({ type: 'Point', coordinates: coordinates ?? [0, 0] });
        return;
      }
      const layersToAdd: DetectorType[] = [];
      Object.entries(DetectorType).forEach((type) => {
        !selectedLayers.includes(type[1]) && layersToAdd.push(type[1]);
      });

      layersToAdd.length > 0 && setSelectedLayers([...selectedLayers, ...layersToAdd]);
      push(`/${ENTITY_DRAWER_VIEW_ALL_ROUTE}/${id}`);
      map.setViewState({
        latitude: coordinates[1],
        longitude: coordinates[0],
        zoom: DEFAULT_ZOOM,
      });
    },
    [
      addMapPin,
      addSearchHistory,
      detectors,
      hasEntityDrawer,
      isAppSearch,
      map,
      onResultClick,
      push,
      selectedLayers,
      setSelectedLayers,
    ]
  );

  useEffect(() => {
    !hasEntityDrawer && searchText?.length > 0 && removeMapPin();
    // only do this when searchText changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  return (
    <>
      {detectors.map((detector) => {
        return (
          <MapSearchResultItem
            key={`searchItem_${detector.id}`}
            name={detector.name}
            onClick={(openInNewWindow) =>
              onClick(detector.id, detector.location?.coordinates, openInNewWindow)
            }
            icon={<CountsIcon />}
            newWindow={isAppSearch}
          />
        );
      })}
    </>
  );
};

export default MapSearchResult;
